import {
  ErrorBoundary,
  SelectItem,
  SelectSeparator,
} from "@prismicio/editor-ui"
import { Suspense } from "react"

import { useRequestRepository } from "../../../hooks/useRequestRepository"
import { useAPIExplorer } from "../../APIExplorerContext"
import { RequestFormInput } from "./RequestFormInput"
import { RequestFormSelect } from "./RequestFormSelect"

/**
 * Represents the default lang value (`undefined`) for Radix UI `<SelectItem />` which does not accept falsy values for an item's value.
 */
const selectItemLangDefault = "selectItemLangDefault"

/**
 * Display a fieldset managing a request document lang setting.
 */
export function RequestLangFieldset() {
  return (
    <ErrorBoundary renderError={() => <RequestLangFielsetError />}>
      <Suspense fallback={null}>
        <RequestLangFielsetContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function RequestLangFielsetContent() {
  const { request } = useAPIExplorer()

  const repository = useRequestRepository()

  if (!request.lang && (!repository || repository.languages.length < 2)) {
    return null
  }

  function setLang(lang: string) {
    request.setLang(lang === selectItemLangDefault ? undefined : lang)
  }

  function humanReadableLang(id?: string) {
    if (!id) {
      const maybeMaster = repository?.languages.find((lang) => lang.is_master)

      return maybeMaster ? `Default (${maybeMaster.id})` : `Default (master)`
    }

    if (id === "*") {
      return "All language (*)"
    }

    const maybeLang = repository?.languages.find((lang) => lang.id === id)

    if (!maybeLang) {
      return id
    }

    return `${maybeLang.name} (${maybeLang.id}${maybeLang.is_master ? " - master" : ""})`
  }

  return (
    <RequestFormSelect
      label="LANG"
      value={request.lang ?? ""}
      onValueChange={setLang}
      placeholder={humanReadableLang()}
    >
      <SelectItem key={selectItemLangDefault} value={selectItemLangDefault}>
        {humanReadableLang()}
      </SelectItem>
      <SelectItem key="*" value="*">
        {humanReadableLang("*")}
      </SelectItem>
      <SelectSeparator />
      {repository?.languages.map(({ id }) => (
        <SelectItem key={id} value={id}>
          {humanReadableLang(id)}
        </SelectItem>
      ))}
    </RequestFormSelect>
  )
}

function RequestLangFielsetError() {
  const { request } = useAPIExplorer()

  function setLang(lang: string) {
    request.setLang(lang || undefined)
  }

  return (
    <RequestFormInput
      value={request.lang ?? ""}
      onValueChange={setLang}
      label="LANG"
      placeholder="Default (master)"
    />
  )
}
